import { SessionMetadataContext } from "contexts/SessionMetadata/context";
import { LabelConfigurationError } from "lib/label-relations";
import { useContext, useEffect, useState } from "react";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { getPrismicTaskInstructions } from "lib/prismic";
import {
  buildResolvedQrTask,
  getResolvedWorkTask,
  ResolvedWorkTask,
} from "lib/work-tasks";
import { isQrCaptureType, PrismicTaskInstructions } from "types";
import { getTaskSteps } from "lib/task-steps";

export function useTaskDefinition({
  taskId,
  workTaskDocumentId,
  enabled,
  reloadKey,
  loadLegacy = true,
}: {
  taskId?: string;
  workTaskDocumentId?: string;
  enabled: boolean;
  reloadKey?: unknown;
  loadLegacy?: boolean;
}) {
  const { config } = useContext(CollectionDetailsContext);
  const { fields: sessionFields } = useContext(SessionMetadataContext);
  const sessionIds = JSON.stringify(sessionFields.map((field) => field.id));
  const selectedCollection = config?.selectedCollection;
  const captureType = taskId ? config?.tasks[taskId]?.capture_type : undefined;
  const [instructions, setInstructions] = useState<
    PrismicTaskInstructions["data"] | null
  >(null);
  const [workTask, setWorkTask] = useState<ResolvedWorkTask | null>(null);
  const [orbTask, setOrbTask] = useState<ResolvedWorkTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled || !taskId) return;

    let cancelled = false;
    setInstructions(null);
    setWorkTask(null);
    setOrbTask(null);
    setLoading(true);
    setError(null);

    if (!workTaskDocumentId && !loadLegacy) {
      setLoading(false);
      return;
    }

    const request = workTaskDocumentId
      ? getResolvedWorkTask(workTaskDocumentId, taskId, selectedCollection)
      : getPrismicTaskInstructions(taskId, selectedCollection);

    request
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setError(new Error(`Task ${taskId} is unavailable`));
          return;
        }
        const activeSessionIds = new Set<string>(JSON.parse(sessionIds));
        const flow = workTaskDocumentId
          ? (result as ResolvedWorkTask)
          : undefined;
        const scopedFields: Array<{ label_id?: unknown }> =
          !activeSessionIds.size
            ? []
            : flow
            ? [
                ...flow.sharedCollectionDetailFields,
                ...flow.steps.flatMap((step) => [
                  ...step.captureCollectionDetailFields,
                  ...step.stepDetailFields,
                  ...(step.rtmsSequence?.fields || []),
                ]),
              ]
            : (
                (result as PrismicTaskInstructions["data"])
                  .instruction_content || []
              )
                .filter((slice) =>
                  ["collection_details", "step_details"].includes(
                    slice.slice_type
                  )
                )
                .flatMap((slice) => slice.items || []);
        const conflict = scopedFields.find((field) =>
          activeSessionIds.has(String(field.label_id || "").trim())
        );
        if (conflict)
          throw new LabelConfigurationError(
            `Label ${conflict.label_id} is configured for both the session and a task. Keep it in Session details only.`
          );
        if (workTaskDocumentId) {
          setWorkTask(result as ResolvedWorkTask);
        } else {
          const data = result as PrismicTaskInstructions["data"];
          if (isQrCaptureType(captureType))
            setOrbTask(buildResolvedQrTask(data, captureType));
          else getTaskSteps(data);
          setInstructions(data);
        }
      })
      .catch((loadError) => {
        console.error(`Failed to load task ${taskId}:`, loadError);
        if (!cancelled) setError(loadError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    captureType,
    loadLegacy,
    reloadKey,
    taskId,
    workTaskDocumentId,
    selectedCollection,
    sessionIds,
  ]);

  return { instructions, workTask, orbTask, loading, error };
}
